/**
 * Partner Test URL Builder — entirely client-side. Nothing entered
 * here is ever transmitted: no fetch, no form action, no analytics
 * event carrying any of the three inputs or the generated URL, no
 * console logging of submitted values. The three fields stay in
 * memory only long enough to construct the URL via the standard
 * URL/URLSearchParams APIs.
 *
 * This tool only constructs a Shopify preview/test URL — it does NOT
 * authorize a partner origin. The actual BPF partner flow still
 * validates the partner storefront origin through the existing
 * partner configuration/approved_embed_origins (src/server/partner-
 * registry.ts, InsightOS) — completely unrelated to and unchanged by
 * this page.
 */
(function () {
  var TEMPLATE_SUFFIX_PATTERN = /^[a-z0-9_-]{1,40}$/i;

  function normalizeProductUrl(raw) {
    var trimmed = (raw || '').trim();
    if (!trimmed) return { ok: false, error: 'Enter the memorial product URL.' };

    var parsed;
    try {
      parsed = new URL(trimmed);
    } catch (e) {
      return { ok: false, error: 'Enter a valid, complete URL (including https://).' };
    }

    if (parsed.protocol !== 'https:') {
      return { ok: false, error: 'The URL must use HTTPS.' };
    }
    if (!parsed.hostname || parsed.hostname.indexOf('.') === -1) {
      return { ok: false, error: 'Enter a valid store domain.' };
    }
    if (!parsed.pathname || parsed.pathname === '/') {
      return { ok: false, error: 'Enter the full product page URL, including its path (e.g. /products/your-memorial).' };
    }

    return { ok: true, value: parsed };
  }

  // Normalizes obvious user mistakes ("?view=test", "view=test") down
  // to the bare suffix, per spec — but never silently strips something
  // that doesn't match a known mistake shape; anything left over must
  // still pass the safe-suffix pattern, or the user sees a clear error
  // instead of a guessed value.
  function normalizeTemplateSuffix(raw) {
    var trimmed = (raw || '').trim();
    if (!trimmed) return { ok: false, error: 'Enter the template name.' };

    if (trimmed.charAt(0) === '?') trimmed = trimmed.slice(1);
    if (/^view=/i.test(trimmed)) trimmed = trimmed.slice('view='.length);
    trimmed = trimmed.trim();

    if (!trimmed) return { ok: false, error: 'Enter the template name.' };
    if (!TEMPLATE_SUFFIX_PATTERN.test(trimmed)) {
      return { ok: false, error: 'Use only letters, numbers, hyphens, or underscores (e.g. "test").' };
    }

    return { ok: true, value: trimmed };
  }

  var THEME_ID_PATH_PATTERN = /\/themes\/(\d+)(?:\/|$)/;

  // V1.1 — accepts EITHER the bare numeric theme ID (unchanged
  // behavior, still rejects a labeled value like "theme=..." rather
  // than stripping it down to digits) OR a full Shopify Theme Editor
  // URL, from which the numeric id is extracted out of the /themes/N
  // path segment. A purely-numeric input is checked FIRST and never
  // run through URL parsing at all, per spec ("do not try to parse it
  // as a URL first"). Requires the URL be on a Shopify admin host —
  // not merely "some URL with a /themes/N in it" — so a copy-pasted
  // link to something else entirely fails closed with a clear message
  // rather than being silently accepted.
  function normalizeThemeIdOrUrl(raw) {
    var trimmed = (raw || '').trim();
    if (!trimmed) return { ok: false, error: 'Enter the Shopify Theme Editor URL or the numeric theme ID.' };

    if (/^[0-9]+$/.test(trimmed)) {
      return { ok: true, value: trimmed };
    }

    var parsed;
    try {
      parsed = new URL(trimmed);
    } catch (e) {
      return { ok: false, error: 'Enter the full Shopify Theme Editor URL or the numeric theme ID.' };
    }

    if (parsed.protocol !== 'https:' || parsed.hostname.toLowerCase() !== 'admin.shopify.com') {
      return { ok: false, error: 'Enter the full Shopify Theme Editor URL or the numeric theme ID.' };
    }

    var match = THEME_ID_PATH_PATTERN.exec(parsed.pathname);
    if (!match) {
      return { ok: false, error: "We couldn't find a Shopify theme ID in that URL. Open the unpublished theme in Shopify and copy the full Theme Editor URL from your browser." };
    }

    return { ok: true, value: match[1] };
  }

  function buildTestUrl(productUrl, templateSuffix, themeId) {
    var url = new URL(productUrl.toString());
    url.searchParams.set('view', templateSuffix);
    url.searchParams.set('preview_theme_id', themeId);
    return url.toString();
  }

  function setFieldError(errorEl, inputEl, message) {
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      inputEl.setAttribute('aria-invalid', 'true');
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
      inputEl.removeAttribute('aria-invalid');
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var temp = document.createElement('textarea');
        temp.value = text;
        temp.setAttribute('readonly', '');
        temp.style.position = 'absolute';
        temp.style.left = '-9999px';
        document.body.appendChild(temp);
        temp.select();
        var succeeded = document.execCommand('copy');
        document.body.removeChild(temp);
        if (succeeded) resolve();
        else reject(new Error('execCommand copy failed'));
      } catch (e) {
        reject(e);
      }
    });
  }

  function initBuilder(root) {
    var form = root.querySelector('[data-url-builder-form]');
    var productUrlInput = root.querySelector('[data-url-builder-product-url]');
    var templateInput = root.querySelector('[data-url-builder-template]');
    var themeIdInput = root.querySelector('[data-url-builder-theme-id]');
    var productUrlError = root.querySelector('[data-url-builder-product-url-error]');
    var templateError = root.querySelector('[data-url-builder-template-error]');
    var themeIdError = root.querySelector('[data-url-builder-theme-id-error]');
    var resultCard = root.querySelector('[data-url-builder-result]');
    var resultField = root.querySelector('[data-url-builder-result-field]');
    var copyButton = root.querySelector('[data-url-builder-copy]');
    var openButton = root.querySelector('[data-url-builder-open]');
    var copyConfirm = root.querySelector('[data-url-builder-copy-confirm]');
    if (!form || !productUrlInput || !templateInput || !themeIdInput || !resultCard || !resultField || !copyButton || !openButton) return;

    var copyConfirmTimer = null;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      resultCard.hidden = true;
      copyConfirm.hidden = true;

      var productUrlResult = normalizeProductUrl(productUrlInput.value);
      setFieldError(productUrlError, productUrlInput, productUrlResult.ok ? null : productUrlResult.error);

      var templateResult = normalizeTemplateSuffix(templateInput.value);
      setFieldError(templateError, templateInput, templateResult.ok ? null : templateResult.error);

      var themeIdResult = normalizeThemeIdOrUrl(themeIdInput.value);
      setFieldError(themeIdError, themeIdInput, themeIdResult.ok ? null : themeIdResult.error);

      if (!productUrlResult.ok || !templateResult.ok || !themeIdResult.ok) {
        var firstInvalid = !productUrlResult.ok ? productUrlInput : !templateResult.ok ? templateInput : themeIdInput;
        firstInvalid.focus();
        return;
      }

      var testUrl = buildTestUrl(productUrlResult.value, templateResult.value, themeIdResult.value);
      resultField.value = testUrl;
      resultCard.hidden = false;
      resultField.focus();
      resultField.select();
    });

    copyButton.addEventListener('click', function () {
      if (!resultField.value) return;
      copyToClipboard(resultField.value)
        .then(function () {
          if (copyConfirmTimer) clearTimeout(copyConfirmTimer);
          copyConfirm.hidden = false;
          copyConfirmTimer = setTimeout(function () {
            copyConfirm.hidden = true;
          }, 2000);
        })
        .catch(function () {
          resultField.focus();
          resultField.select();
        });
    });

    openButton.addEventListener('click', function () {
      if (!resultField.value) return;
      var opened = window.open(resultField.value, '_blank', 'noopener,noreferrer');
      if (!opened) {
        resultField.focus();
        resultField.select();
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-url-builder]');
    roots.forEach(initBuilder);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Test-only export — `module` is undefined in the browser, so this
  // is a no-op there. Lets adapters/builder/shadow-mode-partner-test-
  // url-builder.js exercise the REAL pure functions (URL construction/
  // normalization) directly, rather than only regex-matching source
  // text, matching the precision this URL-encoding-sensitive logic
  // needs.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalizeProductUrl: normalizeProductUrl, normalizeTemplateSuffix: normalizeTemplateSuffix, normalizeThemeIdOrUrl: normalizeThemeIdOrUrl, buildTestUrl: buildTestUrl };
  }
})();
